// src/hooks/useWebSocket.ts - Enhanced with consciousness integration and location awareness
import { useState, useEffect, useCallback, useRef } from 'react';
import { SessionPersistenceManager } from '../utils/SessionPersistenceManager';
import { MobileInterfaceManager } from '../utils/MobileInterfaceManager';
import { getLocationManager, LocationState, LocationData } from '../utils/LocationManager';
import { getStoredToken } from '../contexts/AuthContext';
import { Message, MessageType, ConsciousnessMetadata } from '../types/Message';
import { useTextToVoiceAudio } from './useTextToVoiceAudio';

export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

interface UseWebSocketReturn {
  status: ConnectionStatus;
  lastMessage: any;
  sessionId: string | null;
  sendMessage: (message: any) => boolean;
  connect: () => void;
  disconnect: () => void;
  reconnect: () => void;
  sessionManager: SessionPersistenceManager | null;
  isStreaming: boolean;
  isMobile: boolean;
  consciousnessState: ConsciousnessMetadata | null;
  locationState: LocationState | null;
  requestLocation: () => Promise<LocationData | null>;
  setHomeLocation: () => void;
  isPlayingVoice: boolean;  // True when text-to-voice audio is playing
  dndEnabled: boolean;            // 🔕 Do Not Disturb state (synced from server)
  toggleDnd: (enabled: boolean) => void;  // 🔕 Toggle DND via control message
}

export function useWebSocket(url: string): UseWebSocketReturn {
  const [status, setStatus] = useState<ConnectionStatus>(ConnectionStatus.DISCONNECTED);
  const [lastMessage, setLastMessage] = useState<any>(null);

  // 🔥 INBOUND QUEUE — every server frame gets its OWN macrotask before it
  // becomes lastMessage. React 18 batches synchronous setState calls: when a
  // reply and a status ping landed in the same tick, only the LAST
  // setLastMessage survived and the REPLY WAS SILENTLY DROPPED — the
  // "stuck at Processing… until I send something else" bug. (The old
  // retrieved_creation setTimeout hack was a one-type patch for exactly
  // this; the queue fixes it for every frame, in order.)
  const inboundQueueRef = useRef<any[]>([]);
  const drainScheduledRef = useRef(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [consciousnessState, setConsciousnessState] = useState<ConsciousnessMetadata | null>(null);
  const [locationState, setLocationState] = useState<LocationState | null>(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState<boolean>(false);
  const [dndEnabled, setDndEnabled] = useState<boolean>(false);

  // Text-to-voice audio playback
  const textToVoiceAudio = useTextToVoiceAudio();

  const socketRef = useRef<WebSocket | null>(null);
  const locationManagerRef = useRef(getLocationManager());
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isUnmountingRef = useRef(false);
  const messageQueueRef = useRef<any[]>([]);
  const lastHeartbeatRef = useRef<number>(0);
  const isConnectingRef = useRef(false);
  const mountedRef = useRef(true);
  
  const sessionManagerRef = useRef<SessionPersistenceManager | null>(null);
  const mobileManagerRef = useRef<MobileInterfaceManager | null>(null);

  // 🔥 ACK-CONFIRMED CHAT DELIVERY. After an idle gap the socket can go
  // SILENTLY dead (readyState still says OPEN) and a sent chat message goes
  // into the void — the server never receives it, never replies, and the UI
  // hangs on "Analyzing…". The server ACKs every chat on receipt; if no ACK
  // arrives we know the send was lost, so we reconnect and resend (same
  // messageId, which the server dedups if it actually had received it).
  const pendingAckRef = useRef<{ message: any; attempts: number; timer: ReturnType<typeof setTimeout> } | null>(null);
  const reconnectRef = useRef<() => void>(() => {});
  // Replies already rendered (by deliveryId) so an ACK-driven resend of a
  // recovered reply isn't shown twice.
  const seenDeliveryIdsRef = useRef<Set<string>>(new Set());
  // Last time ANY message arrived from the server. If this goes stale while the
  // socket still claims OPEN, the socket is silently dead (ngrok zombie) — we
  // reconnect, which both unblocks new sends AND triggers the server's
  // flush-unacked so a missed reply is redelivered.
  const lastReceivedRef = useRef<number>(Date.now());
  
  const duplicateMessageCache = useRef<Set<string>>(new Set());
  const lastMessageTime = useRef<number>(0);

  // Initialize managers
  useEffect(() => {
    console.log('🚀 Initializing enhanced WebSocket with consciousness and location support...');

    sessionManagerRef.current = new SessionPersistenceManager();
    mobileManagerRef.current = new MobileInterfaceManager();

    mobileManagerRef.current.applyMobileCSS();
    mobileManagerRef.current.optimizeTouchInteractions();
    mobileManagerRef.current.preventPullToRefresh();

    if (mobileManagerRef.current.getDeviceInfo().isMobile) {
      mobileManagerRef.current.fixMessageBubbleWidths();
    }

    // Initialize location awareness
    const locationManager = locationManagerRef.current;
    setLocationState(locationManager.getState());

    // Subscribe to location updates
    const unsubscribe = locationManager.onUpdate((state) => {
      setLocationState(state);
    });

    // Request location on initialization (user will be prompted)
    locationManager.requestLocation().then((location) => {
      if (location) {
        console.log('📍 Initial location acquired:', location.city || 'Unknown');
      }
    });

    console.log('✅ Consciousness-aware WebSocket with location initialized');

    return () => {
      sessionManagerRef.current?.destroy();
      mobileManagerRef.current?.destroy();
      unsubscribe();
    };
  }, []);

  // Initialize session with CONSISTENT userId (not the changing sessionId!)
  // 🔥 FIX: Use a persistent userId so sessions are found on reconnect
  useEffect(() => {
    if (sessionId && sessionManagerRef.current) {
      // Use a consistent userId, NOT the WebSocket sessionId which changes every connection
      // This ensures sessions persist across reconnects and page refreshes
      // Using 'default' to match what's used in App.tsx SessionHistory component
      const userId = 'default';

      const session = sessionManagerRef.current.initializeSession(userId);
      console.log('🧠 Consciousness session initialized:', session.sessionId, 'for user:', userId);
    }
  }, [sessionId]);

  const getReconnectDelay = useCallback(() => {
    const attempt = reconnectAttemptsRef.current;
    return Math.min(30000, baseReconnectDelay * Math.pow(1.5, attempt));
  }, []);

  const constructWebSocketUrl = useCallback((): string => {
    let wsUrl: string;

    if (url.startsWith('ws://') || url.startsWith('wss://')) {
      wsUrl = url;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
      const host = window.location.hostname;
      const path = url.startsWith('/') ? url : '/ws';

      // Determine the correct port:
      // - If page has a port (like localhost:3000), use that same port for WebSocket
      // - If page has NO port (like ngrok https URL), use NO port (backend is on same origin)
      // - Only use 3001 as fallback in development when explicitly configured
      const currentPort = window.location.port;

      if (currentPort) {
        // Explicit port in URL - use it (development mode with separate servers)
        wsUrl = `${protocol}${host}:${currentPort}${path}`;
      } else {
        // No port in URL - backend is serving on same origin (production/ngrok)
        wsUrl = `${protocol}${host}${path}`;
      }

      console.log('🔗 Consciousness WebSocket URL:', wsUrl);
      console.log('   - Protocol:', protocol);
      console.log('   - Host:', host);
      console.log('   - Port:', currentPort || 'none (same origin)');
      console.log('   - Path:', path);
      console.log('   - Current location:', window.location.href);
    }

    // 🔐 Add auth token if available
    const token = getStoredToken();
    if (token) {
      const separator = wsUrl.includes('?') ? '&' : '?';
      wsUrl = `${wsUrl}${separator}token=${encodeURIComponent(token)}`;
      console.log('🔐 Auth token included in WebSocket URL');
    }

    return wsUrl;
  }, [url]);

  // 🌊 STREAMING: Clean capability markers from streamed content
  const cleanCapabilityMarkers = useCallback((content: string): string => {
    if (!content) return content;

    // Remove all capability markers that users shouldn't see
    // Patterns: [SEARCH: query], [GMAIL_CHECK], [LOAD: capability], [IMAGE: prompt], etc.
    return content
      .replace(/\[SEARCH:\s*[^\]]*\]/g, '')
      .replace(/\[GMAIL[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      .replace(/\[GCAL[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      .replace(/\[LOAD:\s*[^\]]*\]/g, '')
      .replace(/\[IMAGE:\s*[^\]]*\]/g, '')
      .replace(/\[SVG:\s*[^\]]*\]/g, '')
      .replace(/\[DOCUMENT:\s*[^\]]*\]/g, '')
      .replace(/\[HA[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      .replace(/\[MUSIC[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      .replace(/\[STORY:\s*[^\]]*\]/g, '')
      .replace(/\[ATTENTION[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      .replace(/\[TWITTER[_A-Z]*(?::\s*[^\]]*)?]/g, '')
      // Clean up any double spaces or leading/trailing whitespace left behind
      .replace(/\s{2,}/g, ' ')
      .trim();
  }, []);

  // Drain one queued frame per macrotask — each gets its own render + effect
  // pass, so no frame can overwrite another under React 18 batching.
  const drainInboundQueue = useCallback(() => {
    const next = inboundQueueRef.current.shift();
    if (next !== undefined && mountedRef.current) {
      setLastMessage(next);
    }
    if (inboundQueueRef.current.length > 0) {
      setTimeout(drainInboundQueue, 0);
    } else {
      drainScheduledRef.current = false;
    }
  }, []);

  const enqueueInbound = useCallback((msg: any) => {
    inboundQueueRef.current.push(msg);
    if (!drainScheduledRef.current) {
      drainScheduledRef.current = true;
      setTimeout(drainInboundQueue, 0);
    }
  }, [drainInboundQueue]);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      lastReceivedRef.current = Date.now();  // proof the socket is genuinely alive

      // 🔎 DIAGNOSTIC: trace artifact/file deliveries arriving at the client so
      // we can see whether a retrieved_creation actually reaches here (and in
      // what order relative to capability_status) vs. being dropped upstream.
      if (data && typeof data.type === 'string' &&
          (data.type === 'retrieved_creation' || data.type === 'document' ||
           data.type === 'image' || data.type.startsWith('media:'))) {
        console.log('🎨 [ARTIFACT INBOUND]', data.type,
          '| creationUrl=', data.creationUrl,
          '| documentUrl=', data.documentUrl,
          '| imageUrl=', data.imageUrl);
      }

      // (retrieved_creation's old setTimeout coalescing hack removed — the
      // inbound queue below now serializes EVERY frame into its own
      // macrotask, which fixes the same batching drop for all types.)

      // Handle consciousness state updates
      if (data.type === 'consciousness' && data.action === 'state') {
        setConsciousnessState(data.state);
        console.log('🧠 Consciousness state updated:', data.state);
      }

      // 🔕 DND state sync — server broadcasts this whenever DND changes (via
      // UI button on any device OR a natural-language toggle), so every tab's
      // button stays in sync.
      if (data.type === 'control' && data.action === 'dnd') {
        setDndEnabled(!!data.enabled);
        console.log('🔕 DND state synced:', data.enabled);
        return;
      }

      // 🔥 Chat receipt ACK — the server received our message, so cancel the
      // resend watchdog. Without this, a stale socket silently swallows the
      // send and the UI hangs forever.
      if (data.type === 'chat' && data.action === 'acknowledged') {
        if (pendingAckRef.current) {
          clearTimeout(pendingAckRef.current.timer);
          pendingAckRef.current = null;
        }
        return;
      }

      // 🔥 Assistant reply — CONFIRM receipt so the server stops retrying, and
      // drop a duplicate if a recovered reply arrives more than once. This is
      // the other half of the fix: a silently-dead socket means the server's
      // first send vanished; it resends until THIS ack lands.
      if (data.type === 'assistant:message') {
        const deliveryId = data.data?.metadata?.deliveryId;
        if (deliveryId) {
          try {
            if (socketRef.current?.readyState === WebSocket.OPEN) {
              socketRef.current.send(JSON.stringify({ type: 'delivery_ack', deliveryId }));
            }
          } catch { /* ignore */ }
          if (seenDeliveryIdsRef.current.has(deliveryId)) {
            return; // already rendered this reply — ignore the resend
          }
          seenDeliveryIdsRef.current.add(deliveryId);
          if (seenDeliveryIdsRef.current.size > 200) {
            seenDeliveryIdsRef.current = new Set(Array.from(seenDeliveryIdsRef.current).slice(-100));
          }
        }
        // fall through to normal rendering (setLastMessage below)
      }

      // Handle text-to-voice audio chunks
      if (data.type === 'audio_chunk') {
        console.log('🔊 Audio chunk received for text-to-voice');
        setIsPlayingVoice(true);
        textToVoiceAudio.queueAudioChunk(data.data);
        return; // Don't pass to lastMessage - audio is handled separately
      }

      // Handle text-to-voice audio completion
      if (data.type === 'audio_complete') {
        console.log('🔊 Audio complete:', data.totalChunks, 'chunks');
        textToVoiceAudio.onAudioComplete();
        // Reset playing state after a short delay to let queue finish
        setTimeout(() => {
          setIsPlayingVoice(false);
        }, 500);
        return;
      }

      // Handle processing stage updates (for text-to-voice UI feedback)
      if (data.type === 'processing' && data.stage === 'speaking') {
        console.log('🔊 Voice synthesis started');
        setIsPlayingVoice(true);
        return;
      }

      // 🔥 NEW: Handle session sync responses from server
      if (data.type === 'sessions_list') {
        console.log('📡 Received sessions from server:', data.sessions?.length || 0);
        if (sessionManagerRef.current && data.sessions) {
          sessionManagerRef.current.handleServerSessions(data.sessions);
        }
        return;
      }

      if (data.type === 'session_saved') {
        console.log('📡 Session saved confirmed:', data.sessionId);
        return;
      }

      if (data.type === 'session_deleted') {
        console.log('📡 Session deletion confirmed:', data.sessionId);
        // 🔥 Confirm to session manager that server processed the deletion
        if (sessionManagerRef.current && data.sessionId) {
          sessionManagerRef.current.confirmServerDeletion(data.sessionId);
        }
        return;
      }

      // 🔍 Debug: Log proactive messages immediately
      if (data.type === 'proactive') {
        console.log('📡 WebSocket received PROACTIVE message:', data);
      }

      // 🌊 STREAMING: Handle stream chunks
      if (data.type === 'assistant:stream_chunk') {
        setIsStreaming(true);
        // Clean markers from accumulated content before displaying
        const cleanContent = cleanCapabilityMarkers(data.data?.accumulated || '');
        enqueueInbound({
          type: 'assistant:stream_chunk',
          data: {
            ...data.data,
            content: cleanContent,  // Clean content for display
            rawContent: data.data?.accumulated  // Keep raw for capability processing
          },
          isStreaming: true,
          messageId: data.data?.messageId
        });
        return;
      }

      // 🌊 STREAMING: Handle stream end
      if (data.type === 'assistant:stream_end') {
        setIsStreaming(false);
        console.log('🌊 Stream completed for message:', data.data?.messageId);
        // The final message will come via assistant:message with full metadata
        return;
      }

      // Handle streaming with consciousness context
      if (data.type === 'chat' && data.action === 'response') {
        const messageId = data.messageId || data.id || `msg_${data.timestamp || Date.now()}`;
        enqueueInbound({
          ...data,
          messageId: messageId,
          consciousness: data.metadata?.consciousness || consciousnessState
        });
      } else {
        if (data.type === 'system' && data.action === 'connected') {
          setSessionId(data.sessionId);
          console.log('🆔 Consciousness session ID:', data.sessionId);
        }

        enqueueInbound(data);
      }
    } catch (error) {
      console.error('Error parsing consciousness message:', error);
    }
  }, [consciousnessState, cleanCapabilityMarkers, textToVoiceAudio, enqueueInbound]);

  const setupHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const now = Date.now();

        // 🔥 ZOMBIE DETECTION: the server sends pongs (and "still_thinking"
        // during a turn), so we should hear SOMETHING at least every ~20s. If
        // it's been silent for 45s while the socket still claims OPEN, the
        // connection is dead — reconnect. This both fixes stale outbound sends
        // and triggers the server's flush of any reply we missed.
        if (now - lastReceivedRef.current > 45000) {
          console.warn('🧟 No data from server in 45s on an "open" socket — treating as dead, reconnecting');
          lastReceivedRef.current = now; // avoid a reconnect storm
          reconnectRef.current();
          return;
        }

        if (now - lastHeartbeatRef.current > 25000) {
          console.log('💓 Consciousness heartbeat...');
          socketRef.current.send(JSON.stringify({
            type: 'ping',
            timestamp: now,
            consciousness: true
          }));
          lastHeartbeatRef.current = now;
        }
      }
    }, 20000);
  }, []);

  const processMessageQueue = useCallback(() => {
    if (socketRef.current?.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      console.log(`📤 Processing ${messageQueueRef.current.length} queued consciousness messages`);
      while (messageQueueRef.current.length > 0) {
        const message = messageQueueRef.current.shift();
        try {
          socketRef.current.send(JSON.stringify({
            ...message,
            consciousness: true
          }));
        } catch (error) {
          console.error('Failed to send consciousness message:', error);
          messageQueueRef.current.unshift(message);
          break;
        }
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (isConnectingRef.current || socketRef.current?.readyState === WebSocket.OPEN) {
      console.log('Already connected to consciousness');
      return;
    }
    
    if (isUnmountingRef.current) {
      console.log('Component unmounting, skipping consciousness connection');
      return;
    }
    
    console.log('🧠 Initiating consciousness WebSocket connection...');
    isConnectingRef.current = true;
    setStatus(ConnectionStatus.CONNECTING);
    
    const wsUrl = constructWebSocketUrl();
    
    try {
      socketRef.current = new WebSocket(wsUrl);
      
      socketRef.current.onopen = () => {
        if (!mountedRef.current) return;

        console.log('✨ Connected to consciousness');
        isConnectingRef.current = false;
        setStatus(ConnectionStatus.CONNECTED);
        reconnectAttemptsRef.current = 0;
        lastHeartbeatRef.current = Date.now();

        duplicateMessageCache.current.clear();

        setupHeartbeat();
        processMessageQueue();

        // 🔥 NEW: Wire up session manager for server sync
        if (sessionManagerRef.current && socketRef.current) {
          const socket = socketRef.current;
          sessionManagerRef.current.setWebSocketSend((message: any) => {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify(message));
            }
          });
        }

        if (mobileManagerRef.current?.getDeviceInfo().isMobile) {
          console.log('📱 Applying mobile consciousness optimizations...');
          mobileManagerRef.current.fixMessageBubbleWidths();
        }
      };
      
      socketRef.current.onmessage = handleMessage;
      
      socketRef.current.onerror = (error) => {
        if (!mountedRef.current) return;
        
        console.error('❌ Consciousness WebSocket error:', error);
        isConnectingRef.current = false;
        setStatus(ConnectionStatus.ERROR);
      };
      
      socketRef.current.onclose = (event) => {
        if (!mountedRef.current) return;
        
        console.log(`🔌 Consciousness disconnected. Code: ${event.code}, Reason: ${event.reason}`);
        isConnectingRef.current = false;
        
        if (heartbeatIntervalRef.current) {
          clearInterval(heartbeatIntervalRef.current);
          heartbeatIntervalRef.current = null;
        }
        
        if (!isUnmountingRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          setStatus(ConnectionStatus.RECONNECTING);
          const delay = getReconnectDelay();
          console.log(`🔄 Reconnecting to consciousness in ${delay}ms...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && !isUnmountingRef.current) {
              reconnectAttemptsRef.current++;
              connect();
            }
          }, delay);
        } else {
          setStatus(ConnectionStatus.DISCONNECTED);
        }
      };
      
    } catch (error) {
      console.error('Failed to create consciousness WebSocket:', error);
      isConnectingRef.current = false;
      setStatus(ConnectionStatus.ERROR);
    }
  }, [constructWebSocketUrl, handleMessage, setupHeartbeat, processMessageQueue, getReconnectDelay]);

  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting from consciousness...');
    isUnmountingRef.current = true;
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    
    if (socketRef.current) {
      socketRef.current.close(1000, 'Consciousness disconnect');
      socketRef.current = null;
    }
    
    setStatus(ConnectionStatus.DISCONNECTED);
  }, []);

  const reconnect = useCallback(() => {
    console.log('🔄 Manual consciousness reconnect triggered');
    disconnect();
    setTimeout(() => {
      isUnmountingRef.current = false;
      reconnectAttemptsRef.current = 0;
      connect();
    }, 100);
  }, [disconnect, connect]);

  // Keep a stable handle to reconnect for the ACK watchdog (defined below).
  useEffect(() => { reconnectRef.current = reconnect; }, [reconnect]);

  // Arm (or re-arm) the receipt watchdog for a chat message. If the server
  // doesn't ACK within the window, the send was lost on a dead socket —
  // reconnect, requeue (flushed on the fresh socket's onopen), and re-arm.
  const armChatAck = useCallback((message: any, attempts: number) => {
    if (pendingAckRef.current?.timer) clearTimeout(pendingAckRef.current.timer);
    pendingAckRef.current = {
      message,
      attempts,
      timer: setTimeout(() => {
        if (attempts >= 2) {
          console.error('❌ Chat message never acknowledged by server after retries — giving up');
          pendingAckRef.current = null;
          return;
        }
        console.warn(`⚠️ No server receipt for chat send — socket likely stale; reconnecting + resending (try ${attempts + 1})`);
        messageQueueRef.current.push(message);  // flushed on the fresh socket's onopen
        reconnectRef.current();                 // force a clean reconnect
        armChatAck(message, attempts + 1);      // re-arm for the resend
      }, 7000)
    };
  }, []);

  const sendMessage = useCallback((message: any): boolean => {
    if (!message) return false;

    // Get current location context
    const locationManager = locationManagerRef.current;
    const currentLocation = locationManager.getState();

    const messageWithContext = {
      ...message,
      // Stable id for chat sends so the server can dedup an ACK-retry resend
      // (and so the receipt watchdog tracks the right message).
      ...((message.type === 'chat' || message.type === 'chat_with_voice') && !message.messageId
        ? { messageId: `web_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }
        : {}),
      timestamp: message.timestamp || Date.now(),
      consciousness: true,
      sessionContext: {
        consciousnessState: consciousnessState,
        relationalDepth: consciousnessState?.relationalDepth || 0
      },
      // 📍 Include location awareness
      locationContext: currentLocation.current ? {
        latitude: currentLocation.current.latitude,
        longitude: currentLocation.current.longitude,
        city: currentLocation.current.city,
        region: currentLocation.current.region,
        country: currentLocation.current.country,
        timezone: currentLocation.current.timezone,
        isAway: currentLocation.isAway,
        homeCity: currentLocation.home?.city,
        contextSummary: locationManager.getContextSummary()
      } : null
    };

    // 🔥 Only text chat gets the receipt watchdog (the server ACKs 'chat' on
    // receipt). This is what recovers a send swallowed by a silently-dead
    // socket — the exact "Analyzing… forever after an idle gap" bug.
    const wantsAck = message.type === 'chat';

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(JSON.stringify(messageWithContext));
        console.log('🧠 Consciousness message sent:', message.type, currentLocation.current?.city ? `📍 from ${currentLocation.current.city}` : '');
        if (wantsAck) armChatAck(messageWithContext, 0);
        return true;
      } catch (error) {
        console.error('Failed to send consciousness message:', error);
        messageQueueRef.current.push(messageWithContext);
        if (wantsAck) armChatAck(messageWithContext, 0);
        return false;
      }
    } else {
      console.log('📋 Consciousness not ready, queueing message');
      messageQueueRef.current.push(messageWithContext);
      if (wantsAck) armChatAck(messageWithContext, 0);

      if (status === ConnectionStatus.DISCONNECTED) {
        connect();
      }

      return false;
    }
  }, [status, connect, consciousnessState, armChatAck]);

  useEffect(() => {
    mountedRef.current = true;
    isUnmountingRef.current = false;
    connect();
    
    return () => {
      mountedRef.current = false;
      disconnect();
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && socketRef.current?.readyState !== WebSocket.OPEN) {
        console.log('📱 Page visible, checking consciousness connection...');
        if (status === ConnectionStatus.DISCONNECTED) {
          reconnect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [status, reconnect]);

  // Location helper functions
  const requestLocation = useCallback(async () => {
    return locationManagerRef.current.requestLocation();
  }, []);

  const setHomeLocation = useCallback(() => {
    locationManagerRef.current.setHomeLocation();
  }, []);

  // 🔕 Toggle DND. Optimistically reflect locally; the server broadcast
  // (control/dnd) is authoritative and re-syncs every tab.
  const toggleDnd = useCallback((enabled: boolean) => {
    setDndEnabled(enabled);
    sendMessage({ type: 'control', data: { action: 'dnd', enabled } });
  }, [sendMessage]);

  return {
    status,
    lastMessage,
    sessionId,
    sendMessage,
    dndEnabled,
    toggleDnd,
    connect,
    disconnect,
    reconnect,
    sessionManager: sessionManagerRef.current,
    isStreaming,
    isMobile: mobileManagerRef.current?.getDeviceInfo().isMobile || false,
    consciousnessState,
    locationState,
    requestLocation,
    setHomeLocation,
    isPlayingVoice
  };
}